import type { CustomCell, CustomRenderer, Rectangle, Theme } from '@glideapps/glide-data-grid'
import { getMiddleCenterBias, GridCellKind } from '@glideapps/glide-data-grid'

import type { FullTheme } from '../types'

const dateCellEmptyValue = Symbol('date-cell-empty-value')

interface DateCellProps {
  readonly kind: 'date-cell'
  readonly value: number | string | typeof dateCellEmptyValue
}

type DateCell = CustomCell<DateCellProps>

type DateFormaters = {
  full: Intl.DateTimeFormat
  long: Intl.DateTimeFormat
  short: Intl.DateTimeFormat
}

export const dateCellRenderer = (locale: string): CustomRenderer<DateCell> => {
  const dateFormaters: DateFormaters = {
    full: new Intl.DateTimeFormat(locale, {
      dateStyle: 'full',
    }),
    long: new Intl.DateTimeFormat(locale, {
      dateStyle: 'long',
    }),
    short: new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
    }),
  }

  const timeFormater = new Intl.DateTimeFormat(locale, {
    timeStyle: 'short',
  })

  return {
    draw: (args, cell) => {
      const { ctx, theme, rect } = args
      const { value } = cell.data

      if (value === dateCellEmptyValue) {
        return true
      }

      const date = new Date(value)

      if (isNaN(date?.getTime())) {
        // invalid date object
        return true
      }

      const cellWidth = getCellWidth(rect, theme)
      const textY = getCellMiddleY(ctx, rect, theme as FullTheme)
      const textXStart = getCellX(rect, theme)

      const displayTime = timeFormater.format(date)

      const { displayDate, shouldJustifyToRight } = formatDate({
        cellWidth,
        ctx,
        date,
        formaters: dateFormaters,
        time: displayTime,
      })

      drawText({
        align: 'left',
        color: theme.textDark,
        ctx,
        text: displayDate,
        x: textXStart,
        y: textY,
      })

      const dateTextXEnd = textXStart + ctx.measureText(displayDate).width

      drawText({
        align: shouldJustifyToRight ? 'right' : 'left',
        color: theme.textLight,
        ctx,
        text: displayTime,
        x: calculateTimeX({ dateTextXEnd, rect, shouldJustifyToRight, theme }),
        y: textY,
      })

      return true
    },
    isMatch: (c): c is DateCell => (c.data as DateCellProps).kind === 'date-cell',
    kind: GridCellKind.Custom,
  }
}

function formatDate({
  cellWidth,
  ctx,
  date,
  formaters,
  time,
}: {
  date: Date
  formaters: DateFormaters
  time: string
  ctx: CanvasRenderingContext2D
  cellWidth: number
}): { displayDate: string; shouldJustifyToRight: boolean } {
  const fullFormater = formaters.full.format(date)
  const longFormater = formaters.long.format(date)
  const shortFormater = formaters.short.format(date)

  if (cellWidth >= getTextWidth(ctx, fullFormater, time)) {
    return { displayDate: fullFormater, shouldJustifyToRight: true }
  }

  const candidateFormats = [
    {
      format: longFormater,
      width: getTextWidth(ctx, longFormater, time),
    },
    {
      format: shortFormater,
      width: getTextWidth(ctx, shortFormater, time),
    },
  ]

  const displayFormat = candidateFormats.find((format) => format.width <= cellWidth)?.format

  if (!displayFormat) {
    return { displayDate: shortFormater, shouldJustifyToRight: false }
  }

  return { displayDate: displayFormat, shouldJustifyToRight: true }
}

function drawText({
  align,
  color,
  ctx,
  text,
  x,
  y,
}: {
  ctx: CanvasRenderingContext2D
  text: string
  x: number
  y: number
  align: CanvasTextAlign
  color: string
}) {
  ctx.fillStyle = color
  ctx.textAlign = align
  ctx.fillText(text, x, y)
}

function getCellMiddleY(ctx: CanvasRenderingContext2D, rect: Rectangle, theme: FullTheme) {
  const bias = getMiddleCenterBias(ctx, theme)

  return rect.y + rect.height / 2 + bias
}

function getCellWidth(rect: Rectangle, theme: Theme) {
  return rect.width - (theme.cellHorizontalPadding ?? 8) * 2
}

function getCellX(rect: Rectangle, theme: Theme) {
  return rect.x + (theme.cellHorizontalPadding ?? 8)
}

function getTextWidth(ctx: CanvasRenderingContext2D, date: string, time: string) {
  return ctx.measureText(`${date} ${time}`).width
}

function calculateTimeX({
  rect,
  shouldJustifyToRight,
  theme,
  dateTextXEnd,
}: {
  rect: Rectangle
  theme: Theme
  shouldJustifyToRight: boolean
  dateTextXEnd: number
}) {
  if (shouldJustifyToRight) {
    return rect.x + rect.width - (theme.cellHorizontalPadding ?? 8)
  }

  return dateTextXEnd + 5
}
